<!-- OrderHistory.vue -->
 
<template>
  <div class="order-history-container">
    <div class="header">
      <h1>Active Orders for <span style="color: white;">{{ customerName }}</span></h1>
      <div class="menu-bar">
        <!-- User Welcome Message -->
        <!-- <div class="user-welcome" v-if="currentUser.userName">
          Welcome, {{ currentUser.userName }}
        </div> -->
        <div class="menu-item" @click="toggleDropdown('menu')">
          <div class="user-avatar">
            <img src="/image/user_avater.png" alt="User Avatar" class="avatar-img" />
          </div>
          <div class="dropdown-menu menu-dropdown" v-show="activeDropdown === 'menu'">
            <div class="dropdown-item" @click.stop="navigateToDashboard">
              <i class="icon-orders"></i>
              Dashboard
            </div>
            <div class="dropdown-item" @click.stop="logout">
              <i class="icon-logout"></i>
              Logout
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Search Section -->
    <div class="search-section">
      <div class="search-container">
        <div class="search-input-wrapper">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search by Order ID, Name, or Mobile..." 
            class="search-input"
            @input="handleSearchInput"
          />
          <div class="search-icon">🔍</div>
        </div>
        <button 
          v-if="searchQuery" 
          @click="clearSearch" 
          class="clear-search-btn"
        >
          ✕
        </button>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="filter-section">
      <div class="filter-tabs">
        <button 
          v-for="status in filterOptions" 
          :key="status.value"
          @click="setStatusFilter(status.value)"
          class="filter-tab"
          :class="{ 'active': selectedStatusFilter === status.value }"
        >
          {{ status.label }}
          <span v-if="getStatusCount(status.value) > 0" class="count-badge">
            {{ getStatusCount(status.value) }}
          </span>
        </button>
      </div>
    </div>
    
    <div class="orders-content">
      <div v-if="loading" class="loading">
        Loading orders...
      </div>
      
      <div v-else-if="filteredOrders.length === 0 && !searchQuery && selectedStatusFilter === 'all'" class="no-orders">
        No active orders found
      </div>
      
      <div v-else-if="filteredOrders.length === 0" class="no-orders">
        <div v-if="searchQuery">
          No orders found matching "{{ searchQuery }}"
        </div>
        <div v-else>
          No {{ selectedStatusFilter }} orders found
        </div>
      </div>
      
      <div v-else class="orders-list">
        <!-- Results Summary -->
        <div v-if="searchQuery || selectedStatusFilter !== 'all'" class="results-summary">
          Showing {{ filteredOrders.length }} of {{ orders.length }} orders
          <span v-if="searchQuery"> for "{{ searchQuery }}"</span>
          <span v-if="selectedStatusFilter !== 'all'"> with status "{{ selectedStatusFilter }}"</span>
        </div>

        <div 
          v-for="order in filteredOrders" 
          :key="order.order_id"
          class="order-card"
          :class="{ expanded: expandedOrders.includes(order.order_id) }"
        >
          <!-- Order Summary -->
          <div class="order-summary">
            <div class="order-header">
              <div class="order-info">
                <div class="order-id">{{ order.order_id }}</div>
                <div class="order-date">{{ formatDate(order.order_date) }}</div>
                <div class="payment-status">Payment status: {{ order.payment_status }}</div>
              </div>
              <div class="order-totals">
              <div class="total-items">Items: {{ order.total_item_count || order.order_details.length }}</div>
              <div class="total-amount">
                ৳{{ getOrderSummary(order).total.toFixed(2) }}
              </div>
              </div>
            </div>
            
            <div class="customer-info">
              <div class="name">{{ order.user_name }}</div>
              <div class="mobile">{{ order.user_mobile }}</div>
              <div class="address">{{ order.user_address }}</div>
            </div>
            
            <div class="order-meta">
              <div class="payment-method">Payment method: {{ order.payment_method }}</div>
              <div class="order-status" :class="getOrderStatusClass(order.order_status)">
                {{ order.order_status }}
              </div>
            </div>
            
            <button 
              @click="toggleOrderDetails(order.order_id)"
              class="see-details-btn"
            >
              {{ expandedOrders.includes(order.order_id) ? 'Hide Details' : 'See Details' }}
            </button>
          </div>
          
          <!-- Order Details (Expandable) -->
          <div v-if="expandedOrders.includes(order.order_id)" class="order-details">
            <div class="details-header">
              <h3>Order Items</h3>
              <!-- <div class="items-summary">
                Total Items: {{ order.order_details.length }} | 
                Amount: ৳{{ order.total_tp.toFixed(2) }}
              </div> -->
              <button 
                class="select-all-btn"
                @click="toggleSelectAll(order.order_id)"
                :disabled="!canModifyOrder(order.order_status)"
              >
                {{ isAllSelected(order.order_id) ? 'Deselect All' : 'Select all as available' }}
              </button>
            </div>
            
            <div class="items-list">
              <div 
                v-for="(item, index) in order.order_details" 
                :key="`${order.order_id}-${item.item_id}-${index}`"
                class="item-card"
                :class="{ 'acknowledged': !canModifyOrder(order.order_status) }"
              >
                <div class="item-info">
                  <div class="medicine-name">{{ item.item_name }}</div>
                  <div class="medicine-category">{{ item.item_category }} {{ item.item_strength }}</div>
                  <div class="item-details">
                    <span class="quantity">Qty: {{ item.qty }} {{ item.item_pack_type }}  ( 1 {{ item.item_pack_type }} : {{ item.item_pack_amount }} {{ item.item_formation }})</span>
                    <span v-if="item.bonus_qty > 0" class="bonus-qty">
                      + {{ item.bonus_qty }} bonus
                    </span>
                    <span class="amount">৳{{ item.total_amount.toFixed(2) }}</span>
                  </div>
                </div>
                
                <div class="availability-control">
                <div class="availability-dropdown">
                  <select 
                    v-model="item.available"
                    @change="handleAvailabilityChange(order.order_id, item.item_id, item.available)"
                    :disabled="!canModifyOrder(order.order_status)"
                    class="availability-select"
                  >
                    <option value="yes">Available</option>
                    <option value="partially">Partially Available</option>
                    <option value="no">Not Available</option>
                  </select>
                </div>
                
                <div v-if="item.available === 'partially'" class="quantity-input-wrapper">
                  <input 
                    type="number" 
                    v-model.number="item.available_qty"
                    @input="handleQuantityChange(order.order_id, item.item_id, item.available_qty)"
                    :max="item.qty"
                    :min="0"
                    :disabled="!canModifyOrder(order.order_status)"
                    class="quantity-input"
                    :placeholder="`Max ${item.qty}`"
                  />
                  <span class="quantity-unit">{{ item.item_pack_type }}</span>
                </div>
                
                <div v-if="!canModifyOrder(order.order_status)" class="status-display">
                  <span class="status-badge" :class="getAvailabilityClass(item.available)">
                    {{ getAvailabilityText(item.available, item.available_qty, item.qty) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="note-section" v-if="order.note">
                <h3>Order note: {{order.note}}</h3>
            </div>
            <!-- Order Summary Section -->
            <div class="order-summary-section">
              <h3>Order Summary</h3>
              <div class="summary-details">
                <div class="summary-row">
                  <span class="summary-label">Total items:</span>
                  <span class="summary-value">{{ getOrderSummary(order).totalQuantity }} items</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">Subtotal:</span>
                  <span class="summary-value">৳{{ getOrderSummary(order).subtotal.toFixed(2) }}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">Discount:</span>
                  <span class="summary-value discount-amount">
                    -৳{{ getOrderSummary(order).discountAmount.toFixed(2) }}
                    <button 
                      v-if="orderDiscounts[order.order_id] && 
                            orderDiscounts[order.order_id].value > 0 && 
                            canModifyOrder(order.order_status)"
                      @click="clearDiscount(order.order_id)" 
                      class="clear-discount-btn"
                      title="Clear discount"
                    >
                      ✕
                    </button>

                    <!-- <button 
                      v-if="orderDiscounts[order.order_id] && orderDiscounts[order.order_id].value > 0"
                      @click="clearDiscount(order.order_id)" :disabled="!canModifyOrder(order.order_status)"
                      class="clear-discount-btn"
                      title="Clear discount"
                    >
                      ✕
                    </button> -->
                  </span>
                </div>
                <div class="summary-row total-row">
                  <span class="summary-label">Total:</span>
                  <span class="summary-value total-amount">৳{{ getOrderSummary(order).total.toFixed(2) }}</span>
                </div>
              </div>
              
              <!-- Discount Controls -->
              <div class="discount-controls">
                <button 
                  @click="toggleDiscountOptions(order.order_id)"
                  class="give-discount-btn"
                  :class="{ 'active': showDiscountOptions[order.order_id] }"
                  :disabled="!canModifyOrder(order.order_status)"
                >
                  {{ showDiscountOptions[order.order_id] ? 'Hide Discount Options' : 'Give Discount' }}
                </button>
                
                <!-- Discount Options Dropdown -->
                <div v-if="showDiscountOptions[order.order_id]" class="discount-options">
                  <div class="discount-type-selection">
                    <label class="discount-radio">
                      <input 
                        type="radio" 
                        :name="`discount-type-${order.order_id}`"
                        value="percentage"
                        @change="setDiscountType(order.order_id, 'percentage')"
                      />
                      Percentage (%)
                    </label>
                    <label class="discount-radio">
                      <input 
                        type="radio" 
                        :name="`discount-type-${order.order_id}`"
                        value="fixed"
                        @change="setDiscountType(order.order_id, 'fixed')"
                      />
                      Fixed Amount
                    </label>
                  </div>
                  
                  <div v-if="orderDiscounts[order.order_id] && orderDiscounts[order.order_id].type" class="discount-input-section">
                    <div class="discount-input-wrapper">
                      <input 
                        type="number"
                        :placeholder="orderDiscounts[order.order_id].type === 'percentage' ? 'Enter percentage' : 'Enter amount'"
                        :max="orderDiscounts[order.order_id].type === 'percentage' ? 100 : getOrderSummary(order).subtotal"
                        min="0"
                        step="0.01"
                        v-model="orderDiscounts[order.order_id].value"
                        @input="updateDiscountValue(order.order_id, $event.target.value)"
                        class="discount-input"
                      />
                      <span class="discount-unit">
                        {{ orderDiscounts[order.order_id].type === 'percentage' ? '%' : '৳' }}
                      </span>
                    </div>
                    <button 
                      @click="applyDiscount(order.order_id)"
                      class="apply-discount-btn"
                      :disabled="!orderDiscounts[order.order_id].value || orderDiscounts[order.order_id].value <= 0"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- <div class="acknowledge-section" v-if="order.order_status === 'pending'">
              <button 
                @click="acknowledgeOrder(order.order_id)"
                class="acknowledge-btn"
              :disabled="!hasAvailableItems(order) || acknowledgingOrder === order.order_id"
              >
                {{ acknowledgingOrder === order.order_id ? 'Processing...' : 'Acknowledge Available Items' }}
              </button>
            </div> -->
            <div class="acknowledge-section" v-if="order.order_status === 'pending'">
              <button 
                @click="acknowledgeOrder(order.order_id)"
                class="acknowledge-btn"
              >
                {{ acknowledgingOrder === order.order_id ? 'Processing...' : 'Acknowledge Available Items' }}
              </button>
            </div>
    
            <!-- Order Status Section -->
            <div class="status-section" v-else>
              <div class="status-message" :class="getOrderStatusClass(order.order_status)">
                {{ getStatusMessage(order.order_status) }} {{ getStatusIcon(order.order_status) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Toast Message -->
    <div v-if="toastMessage" class="toast" :class="toastType">
      {{ toastMessage }}
    </div>
  </div>
  </div>
</template>

<script>
export default {
  name: 'OrderHistory',
  data() {
    return {
      orders: [],
      loading: true,
      activeDropdown: null,
      expandedOrders: [],
      customerName: '',
      userId: '',
      toastMessage: '',
      toastType: '',
      acknowledgingOrder: null,
      orderDiscounts: {}, 
      showDiscountOptions: {},
      searchQuery: '',
      selectedStatusFilter: 'all',
      filterOptions: [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'Acknowledged', value: 'acknowledged' },
        { label: 'Confirmed', value: 'confirmed' },
        { label: 'Cancelled', value: 'cancelled' }
      ]
    }
  },
  computed: {
    filteredOrders() {
      let filtered = [...this.orders]
      
      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim()
        filtered = filtered.filter(order => {
          return (
            order.order_id.toLowerCase().includes(query) ||
            order.user_name.toLowerCase().includes(query) ||
            order.user_mobile.includes(query)
          )
        })
      }
      
      // Apply status filter
      if (this.selectedStatusFilter !== 'all') {
        filtered = filtered.filter(order => 
          order.order_status.toLowerCase() === this.selectedStatusFilter.toLowerCase()
        )
      }
      
      return filtered
    },
    getOrderSummary() {
    return (order) => {
      // For acknowledged orders, use database values directly
      if (order.order_status === 'acknowledged') {

        return {
          totalQuantity: order.total_item_count || 0,
          subtotal: order.total_discount + order.total_amount || 0,
          discountAmount: order.total_discount || 0,
          total: order.total_amount || order.total_tp || 0
        }
      }

      if (order.order_status === 'confirmed') {

        return {
          totalQuantity: order.total_item_count || 0,
          subtotal: order.total_discount + order.total_amount || 0,
          discountAmount: order.total_discount || 0,
          total: order.total_amount || 0
        }
      }
      
      
      // For pending orders, calculate based on available items and quantities
      let totalQuantity = 0
      let subtotal = 0
      
      order.order_details.forEach(item => {
        if (item.available === 'yes') {
          // Full quantity and amount
          totalQuantity += item.qty || 0
          subtotal += item.total_amount || 0
        } else if (item.available === 'partially' && item.available_qty > 0) {
          // Partial quantity and proportional amount
          const originalQty = item.qty || 0
          const availableQty = item.available_qty || 0
          const originalAmount = item.total_amount || 0
          
          if (originalQty > 0) {
            const proportionalAmount = (originalAmount / originalQty) * availableQty
            totalQuantity += availableQty
            subtotal += proportionalAmount
          }
        }
        // If available === 'no', don't add anything to totals
      })
      
      let discountAmount = 0
      let total = 0
      
      // Calculate based on applied discount
      const orderDiscount = this.orderDiscounts[order.order_id] || {}
      
      if (orderDiscount.type === 'percentage' && orderDiscount.value > 0) {
        discountAmount = (subtotal * orderDiscount.value) / 100
      } else if (orderDiscount.type === 'fixed' && orderDiscount.value > 0) {
        discountAmount = Math.min(orderDiscount.value, subtotal) // Don't exceed subtotal
      }
      
      total = Math.max(0, subtotal - discountAmount) // Ensure total doesn't go negative
      
      return {
        totalQuantity,
        subtotal,
        discountAmount,
        total
      }
    }
  },
  },
  mounted() {
    this.checkLogin()
    this.loadOrderHistory()
  },
  methods: {
    getItemDisplayAmount(item) {
      if (item.available === 'yes') {
        return item.total_amount
      } else if (item.available === 'partially' && item.available_qty > 0) {
        const originalQty = item.qty || 0
        const availableQty = item.available_qty || 0
        const originalAmount = item.total_amount || 0
        
        if (originalQty > 0) {
          return (originalAmount / originalQty) * availableQty
        }
      }
      return 0
    },
    // Save discount state to localStorage for acknowledged orders
    saveDiscountState(orderId, discountInfo) {
      try {
        localStorage.setItem(`order_discount_${orderId}`, JSON.stringify(discountInfo))
      } catch (error) {
        console.error('Error saving discount state:', error)
      }
    },

    // Load discount state from localStorage
    loadDiscountState(orderId) {
      try {
        const savedDiscount = localStorage.getItem(`order_discount_${orderId}`)
        return savedDiscount ? JSON.parse(savedDiscount) : null
      } catch (error) {
        console.error('Error loading discount state:', error)
        return null
      }
    },
    checkLogin() {
      const isLoggedIn = localStorage.getItem('isLoggedIn')
      if (!isLoggedIn) {
        this.$router.push('/')
        return
      }
      
      this.customerName = localStorage.getItem('customerName') || 'Customer'
      this.userId = localStorage.getItem('userId')
    },

     // Toggle discount options visibility
    toggleDiscountOptions(orderId) {
      this.showDiscountOptions = {
        ...this.showDiscountOptions,
        [orderId]: !this.showDiscountOptions[orderId]
      }
    },
    
    // Set discount type for an order
    setDiscountType(orderId, type) {
      if (!this.orderDiscounts[orderId]) {
        this.orderDiscounts = {
          ...this.orderDiscounts,
          [orderId]: {}
        }
      }
      this.orderDiscounts = {
        ...this.orderDiscounts,
        [orderId]: {
          ...this.orderDiscounts[orderId],
          type: type,
          value: 0
        }
      }
    },
    
    // Update discount value
    updateDiscountValue(orderId, value) {
      if (!this.orderDiscounts[orderId]) {
        this.orderDiscounts = {
          ...this.orderDiscounts,
          [orderId]: {}
        }
      }
      this.orderDiscounts = {
        ...this.orderDiscounts,
        [orderId]: {
          ...this.orderDiscounts[orderId],
          value: parseFloat(value) || 0
        }
      }
    },
    
    // Apply discount and hide options
    applyDiscount(orderId) {
      const discount = this.orderDiscounts[orderId]
      if (discount && discount.value > 0) {
        this.showDiscountOptions = {
          ...this.showDiscountOptions,
          [orderId]: false
        }
        this.showToast(`Discount applied successfully`, 'success')
      } else {
        this.showToast('Please enter a valid discount value', 'error')
      }
    },
    
    // Clear discount for an order
    clearDiscount(orderId) {
      this.orderDiscounts = {
        ...this.orderDiscounts,
        [orderId]: {}
      }
      this.showDiscountOptions = {
        ...this.showDiscountOptions,
        [orderId]: false
      }
      this.showToast('Discount cleared', 'success')
    },

    // Load order history from API
    async loadOrderHistory() {
      try {
        this.loading = true
        // Remove payment_status filter to get all orders including acknowledged ones
        const response = await fetch(`https://e01.yeapps.com/medi360/order_history/${this.userId}?limit=50`)
        const data = await response.json()
        
        if (data.order_data && data.order_data[0] && data.order_data[0].status === 'Success') {
          this.orders = data.order_data[0].active_order_list.map(order => {
            // Load saved acknowledgment state from localStorage (only for pending orders)
            const savedState = this.getOrderAcknowledgmentState(order.order_id)
            
            // For acknowledged orders, preserve discount information from database
            if (order.order_status === 'acknowledged' && order.total_discount > 0) {
              // Calculate discount type and value from database values
              const subtotal = order.total_tp || 0
              const discountAmount = order.total_discount
              
              if (subtotal > 0) {
                // Try to determine if it was percentage or fixed discount
                const discountPercentage = (discountAmount / subtotal) * 100
                const isLikelyPercentage = discountPercentage % 1 === 0 && discountPercentage <= 100
                
                this.orderDiscounts = {
                  ...this.orderDiscounts,
                  [order.order_id]: {
                    type: isLikelyPercentage ? 'percentage' : 'fixed',
                    value: isLikelyPercentage ? discountPercentage : discountAmount
                  }
                }
              }
            }
            
            return {
              ...order,
              // Always use fresh order_status from database
              order_status: order.order_status,
              order_details: order.order_details.map(item => {
                // For acknowledged orders, use database values
                if (order.order_status === 'acknowledged') {
                  return {
                    ...item,
                    // Use availability data from database
                    available: item.available || 'yes',
                    available_qty: item.available_qty || item.qty || 0
                  }
                } else {
                  // For pending orders, use saved state if available, otherwise default to 'yes'
                  const savedItem = savedState ? 
                    savedState.items.find(savedItem => savedItem.item_id === item.item_id) : 
                    undefined
                  
                  return {
                    ...item,
                    available: savedItem?.available || 'yes',
                    available_qty: savedItem?.available_qty || item.qty || 0
                  }
                }
              })
            }
          })
        } else {
          this.orders = []
        }
      } catch (error) {
        console.error('Error loading order history:', error)
        this.showToast('Error loading orders', 'error')
      } finally {
        this.loading = false
      }
    },
    // New search methods
    handleSearchInput() {
      // Optional: Add debouncing here if needed for performance
      // For now, search happens immediately as user types
    },
    
    clearSearch() {
      this.searchQuery = ''
    },
    
    // New filter methods
    setStatusFilter(status) {
      this.selectedStatusFilter = status
    },
    
    getStatusCount(status) {
      if (status === 'all') {
        return this.orders.length
      }
      return this.orders.filter(order => 
        order.order_status.toLowerCase() === status.toLowerCase()
      ).length
    },
    
    // Modified: Get saved acknowledgment state from localStorage (only item availability, not order status)
    getOrderAcknowledgmentState(orderId) {
      try {
        const savedState = localStorage.getItem(`order_ack_${orderId}`)
        return savedState ? JSON.parse(savedState) : null
      } catch (error) {
        console.error('Error reading saved order state:', error)
        return null
      }
    },
    toggleDropdown(dropdown) {
      if (this.activeDropdown === dropdown) {
        this.activeDropdown = null;
      } else {
        this.activeDropdown = dropdown;
      }
    },
    navigateToDashboard() {
      this.activeDropdown = null;
      console.log('Navigating to Dashboard page');
      this.$router.push({
        name: 'OrderHistory'
      });
    },
    logout() {
      this.activeDropdown = null;
      console.log('User logging out');
      
      // Clear all stored user data
      const keysToRemove = ['isLoggedIn', 'customer_id', 'customerName', 'status']
      keysToRemove.forEach(key => {
        localStorage.removeItem(key)
      })
      
      // Clear session storage as well
      sessionStorage.clear()
      
      // Redirect to login page
      this.$router.push({
        name: 'LoginForm'
      });
    },

    // Close dropdowns when clicking outside
    closeDropdowns(event) {
      if (!event.target.closest('.menu-item')) {
        this.activeDropdown = null;
      }
    },

    // Save acknowledgment state to localStorage
    saveOrderAcknowledgmentState(orderId, items) {
      try {
        const stateToSave = {
          items: items.map(item => ({
            item_id: item.item_id,
            available: item.available,
            available_qty: item.available_qty
          })),
          timestamp: new Date().toISOString()
        }
        localStorage.setItem(`order_ack_${orderId}`, JSON.stringify(stateToSave))
      } catch (error) {
        console.error('Error saving order state:', error)
      }
    },
    
    toggleOrderDetails(orderId) {
      const index = this.expandedOrders.indexOf(orderId)
      if (index > -1) {
        this.expandedOrders.splice(index, 1)
      } else {
        this.expandedOrders.push(orderId)
      }
    },
    
    toggleSelectAll(orderId) {
      const order = this.orders.find(o => o.order_id === orderId)
      if (!order) return

      const allSelected = this.isAllSelected(orderId)
      order.order_details.forEach(item => {
        item.available = allSelected ? 'no' : 'yes'
        if (item.available === 'yes') {
          item.available_qty = item.qty
        }
      })
      
      // Save the updated item availability to localStorage
      this.saveOrderAcknowledgmentState(orderId, order.order_details)
    },

    isAllSelected(orderId) {
      const order = this.orders.find(o => o.order_id === orderId)
      if (!order) return false
      return order.order_details.every(item => item.available === 'yes')
    },

    //HANDLING AVAILABILITY CHANGE
    handleAvailabilityChange(orderId, itemId, availability) {
      const order = this.orders.find(o => o.order_id === orderId)
      if (order) {
        const item = order.order_details.find(i => i.item_id === itemId)
        if (item) {
          item.available = availability
          
          // Set default quantity based on availability
          if (availability === 'yes') {
            item.available_qty = item.qty
          } else if (availability === 'partially') {
            item.available_qty = Math.min(1, item.qty) // Default to 1 or original qty if less
          } else {
            item.available_qty = 0
          }
          
          // Save the updated item availability to localStorage
          this.saveOrderAcknowledgmentState(orderId, order.order_details)
        }
      }
    },

    //HANDLING QUANTITY CHANGE
    handleQuantityChange(orderId, itemId, quantity) {
      const order = this.orders.find(o => o.order_id === orderId)
      if (order) {
        const item = order.order_details.find(i => i.item_id === itemId)
        if (item) {
          // Ensure quantity doesn't exceed original quantity
          const maxQty = parseInt(item.qty) || 0
          const newQty = Math.min(Math.max(0, parseInt(quantity) || 0), maxQty)
          
          item.available_qty = newQty
          
          // Save the updated item availability to localStorage
          this.saveOrderAcknowledgmentState(orderId, order.order_details)
        }
      }
    },



    updateItemAvailability(orderId, itemId, available) {
      // Find the order and item, then update availability
      const order = this.orders.find(o => o.order_id === orderId)
      if (order) {
        const item = order.order_details.find(i => i.item_id === itemId)
        if (item) {
          item.available = available
          
          // Save the updated item availability to localStorage
          this.saveOrderAcknowledgmentState(orderId, order.order_details)
        }
      }
    },
    
    hasAvailableItems(order) {
      return order.order_details.some(item => 
        item.available === 'yes' || 
        (item.available === 'partially' && item.available_qty > 0) 
      )
    },
    
    // New method: Check if order can be modified (only pending orders can be modified)
    canModifyOrder(orderStatus) {
      return orderStatus === 'pending'
    },
    
    getAvailabilityClass(availability) {
      switch(availability) {
        case 'yes': return 'status-available'
        case 'partially': return 'status-partial'
        case 'no': return 'status-unavailable'
        default: return 'status-default'
      }
    },

    getAvailabilityText(availability, availableQty, totalQty) {
      switch(availability) {
        case 'yes': 
          return 'Available'
        case 'partially': 
          return `${availableQty || 0}/${totalQty} Available`
        case 'no': 
          return 'Not Available'
        default: 
          return 'Unknown'
      }
    },

    // New method: Get status message
    getStatusMessage(status) {
      switch(status) {
        case 'acknowledged':
          return 'This order has been acknowledged'
        case 'confirmed':
          return 'This order has been confirmed'
        case 'cancelled':
          return 'This order has been cancelled'
        case 'delivered':
          return 'This order has been delivered'
        case 'pending':
          return 'This order is pending'
        default:
          return `Order status: ${status}`
      }
    },
    
    // New method: Get status icon
    getStatusIcon(status) {
      switch(status) {
        case 'acknowledged':
          return '✅'
        case 'confirmed':
          return '✅'
        case 'cancelled':
          return '🚫'
        case 'delivered':
          return '✅'
        case 'pending':
          return '⏳'
        default:
          return ''
      }
    },
    
    async acknowledgeOrder(orderId) {
      const order = this.orders.find(o => o.order_id === orderId)
      if (!order) return
      
      // Check if there are any available items (full or partial)
      const hasAvailableItems = order.order_details.some(item => 
        item.available === 'yes' || 
        (item.available === 'partially' && item.available_qty > 0)
      )
      
      if (!hasAvailableItems) {
        this.showToast('Please select at least one available item', 'error')
        return
      }
      
      try {
        this.acknowledgingOrder = orderId
        
        // Calculate totals using the updated order summary
        const orderSummary = this.getOrderSummary(order)
        
        // Prepare data for API with calculated amounts for each item
        const requestData = {
          order_id: orderId,
          customer_id: this.userId,
          total_discount: orderSummary.discountAmount,
          total_amount: orderSummary.total,
          available_items: order.order_details.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            qty: item.qty,
            available: item.available,
            available_qty: item.available_qty || 0,
            total_discount: item.total_discount || 0,
            // Calculate proportional amount based on availability
            total_amount: this.getItemDisplayAmount(item)
          }))
        }
        
        // Send acknowledgment to API
        const response = await fetch('https://e01.yeapps.com/medi360/acknowledge_order', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        })
        
        const data = await response.json()
        
        if (data.success) {
          this.showToast(data.message || 'Order Acknowledged', 'success')
          
          // Update the order status and totals locally
          const orderIndex = this.orders.findIndex(o => o.order_id === orderId)
          if (orderIndex !== -1) {
            this.orders[orderIndex].order_status = 'acknowledged'
            this.orders[orderIndex].total_tp = orderSummary.total
            this.orders[orderIndex].total_discount = orderSummary.discountAmount
            
            // Save the discount state to localStorage
            if (this.orderDiscounts[orderId]) {
              this.saveDiscountState(orderId, this.orderDiscounts[orderId])
            }
            
            // Save the item availability to localStorage
            this.saveOrderAcknowledgmentState(
              orderId, 
              this.orders[orderIndex].order_details
            )
          }
          
        } else {
          this.showToast(data.error || 'Failed to acknowledge order', 'error')
        }
        
      } catch (error) {
        console.error('Error acknowledging order:', error)
        this.showToast('Network error while acknowledging order', 'error')
      } finally {
        this.acknowledgingOrder = null
      }
    },
    
    getOrderStatusClass(status) {
      switch(status) {
        case 'confirmed':
          return 'status-confirmed'
        case 'cancelled':
          return 'status-cancelled'
        case 'delivered':
          return 'status-delivered'
        case 'pending':
          return 'status-pending'
        case 'acknowledged':
          return 'status-acknowledged'
        default:
          return 'status-default'
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
    },
    
    showToast(message, type) {
      this.toastMessage = message
      this.toastType = type
      setTimeout(() => {
        this.toastMessage = ''
        this.toastType = ''
      }, 3000)
    }
  },
  created() {
    // Close dropdowns when clicking outside
    document.addEventListener('click', this.closeDropdowns)
  },
  beforeUnmount() {
    // Remove event listener when component is destroyed
    document.removeEventListener('click', this.closeDropdowns);
  }
}
</script>

<style scoped>
.order-history-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  background-color: #000;
  color: #e2e2e2;
  max-width: 480px;
  margin: 0 auto;
  min-height: 50vh;
}

.header {
  display: flex;
  align-items: center;
  padding: 12px;
  position: sticky;
  top: 0;
  background-color: #000;
  z-index: 10;
  border-bottom: 1px solid #333;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

.header h1 {
  margin: 0;
  color: #3aa757;
  font-size: 14px;
  flex: 1;
  text-align: left;
}

.menu-bar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.menu-item {
  position: relative;
  cursor: pointer;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #3aa757;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border: 1px solid #444;
  border-radius: 6px;
  min-width: 120px;
  max-width: 140px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  z-index: 1001;
  overflow: hidden;
  margin-top: 4px;
  backdrop-filter: blur(10px);
}

.dropdown-item {
  padding: 8px 12px;
  right: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #e2e2e2;
  transition: all 0.2s ease;
  border-bottom: 1px solid #333;
}

.dropdown-item:hover {
  background: linear-gradient(145deg, #2d994a, #2a9a47);
  color: white;
  transform: translateX(2px);
}

.dropdown-item:first-child {
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}

.dropdown-item:last-child {
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}

/* New Search Section Styles */
.search-section {
  padding: 8px;
  background-color: #000;
  border-bottom: 1px solid #333;
}

.search-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
}

.search-input {
  width: 100%;
  padding: 8px 35px 8px 12px;
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #e2e2e2;
  font-size: 11px;
  outline: none;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.search-input:focus {
  border-color: #3aa757;
}

.search-input::placeholder {
  color: #666;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 11px;
  pointer-events: none;
}

.clear-search-btn {
  background-color: #333;
  color: #e2e2e2;
  border: none;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clear-search-btn:hover {
  background-color: #555;
}

/* New Filter Section Styles */
.filter-section {
  padding: 8px 8px;
  border-radius: 8px;
  background-color: #0a0a0a;
  border-bottom: 1px solid #333;
}

.filter-tabs {
  display: flex;
  gap: 4px;
  width: 100%;
  justify-content: space-between;
}

.filter-tabs::-webkit-scrollbar {
  display: none;
}
.filter-tab {
  padding: 4px 8px;
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  color: #999;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  text-align: center;
}

.filter-tab:hover {
  background-color: #2a2a2a;
  border-color: #444;
}

.filter-tab.active {
  background-color: #3aa757;
  border-color: #3aa757;
  color: #e2e2e2;
}

.count-badge {
  background-color: rgba(255, 255, 255, 0.2);
  color: inherit;
  border-radius: 10px;
  padding: 1px 5px;
  font-size: 11px;
  font-weight: 600;
  min-width: 16px;
  text-align: center;
}

.filter-tab.active .count-badge {
  background-color: rgba(0, 0, 0, 0.2);
}

/* Results Summary */
.results-summary {
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
  color: #999;
  font-size: 11px;
  text-align: center;
}

.orders-content {
  padding: 8px;
}

.loading, .no-orders {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-card {
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  border-left: 3px solid #333;
}

.order-card.expanded {
  border-color: #3aa757;
}

/* Order Card Status Indicators */
.order-card:has(.status-pending) {
  border-left-color: #ffd43b;
}

.order-card:has(.status-acknowledged) {
  border-left-color: #74c0fc;
}

.order-card:has(.status-confirmed) {
  border-left-color: #51cf66;
}

.order-card:has(.status-cancelled) {
  border-left-color: #ff6b6b;
}

.order-card:has(.status-delivered) {
  border-left-color: #868e96;
}

.order-summary {
  padding: 12px;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2px;
}

.order-info .order-id {
  font-weight: bold;
  color: #3aa757;
  font-size: 14px;
}

.order-info .order-date {
  color: #999;
  font-size: 11px;
  margin-top: 2px;
}

.order-info .payment-status {
  background-color: #333;
  color: #e2e2e2;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  margin-top: 3px;
  display: inline-block;
}

.order-totals {
  text-align: right;
}

.order-totals .total-items {
  color: #999;
  font-size: 11px;
}

.order-totals .total-amount {
  font-weight: bold;
  color: #3aa757;
  font-size: 14px;
}

.customer-info {
  margin-bottom: 8px;
  padding: 6px 0;
  border-top: 1px solid #333;
  border-bottom: 1px solid #333;
}

.customer-info .name {
  font-weight: 500;
  margin-bottom: 3px;
  font-size: 14px;
}

.customer-info .mobile {
  color: #999;
  font-size: 11px;
  margin-bottom: 2px;
}

.customer-info .address {
  color: #999;
  font-size: 11px;
}

.order-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 11px;
}

.payment-method {
  color: #999;
}

/* Order Status Colors - Dark Theme Adapted */
.order-status {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
}

.status-pending {
  background-color: #2d2006;
  color: #ffd43b;
  border: 1px solid #ffd43b;
}

.status-acknowledged {
  background-color: #0c2947;
  color: #74c0fc;
  border: 1px solid #74c0fc;
}

.status-confirmed {
  background-color: #0a3818;
  color: #51cf66;
  border: 1px solid #51cf66;
}

.status-cancelled {
  background-color: #2b0e0e;
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
}

.status-delivered {
  background-color: #212529;
  color: #868e96;
  border: 1px solid #868e96;
}

.status-default {
  background-color: #333;
  color: #999;
  border: 1px solid #555;
}

.see-details-btn {
  width: 100%;
  background-color: #3aa757;
  color: #e2e2e2;
  border: none;
  border-radius: 6px;
  padding: 8px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
}

.see-details-btn:hover {
  background-color: #2e8a47;
}

.order-details {
  background-color: #111;
  border-top: 1px solid #333;
  padding: 12px;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.details-header h3 {
  margin: 0;
  color: #3aa757;
  font-size: 14px;
}

.note-section{
  color: #e2e2e2;
  font-size: 11px;
}

.items-summary {
  color: #999;
  font-size: 11px;
}

.select-all-btn {
  background-color: #333;
  color: #e2e2e2;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.select-all-btn:hover:not(:disabled) {
  background-color: #555;
}

.select-all-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.items-list {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.item-card {
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  transition: all 0.3s ease;
}

.item-card.acknowledged {
  opacity: 0.8;
  border-color: #555;
}

.item-info {
  flex: 1;
}

.medicine-name {
  font-weight: 500;
  color: #e2e2e2;
  font-size: 14px;
  margin-bottom: 4px;
}

.medicine-category {
  color: #999;
  font-size: 11px;
  margin-bottom: 6px;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.quantity {
  color: #bbb;
  font-size: 11px;
}

.bonus-qty {
  color: #3aa757;
  font-size: 11px;
  font-weight: 500;
}

.amount {
  color: #3aa757;
  font-weight: bold;
  font-size: 11px;
}

.availability-control {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
  min-width: 120px;
}

.availability-dropdown {
  width: 100%;
}

.availability-select {
  width: 100%;
  padding: 6px 8px;
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  color: #e2e2e2;
  font-size: 11px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.3s ease;
}

.availability-select:focus {
  border-color: #3aa757;
}

.availability-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #2a2a2a;
}

.availability-select option {
  background-color: #1a1a1a;
  color: #e2e2e2;
  padding: 4px;
}

.quantity-input-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.quantity-input {
  flex: 1;
  padding: 4px 6px;
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  color: #e2e2e2;
  font-size: 11px;
  outline: none;
  transition: border-color 0.3s ease;
  text-align: center;
}

.quantity-input:focus {
  border-color: #3aa757;
}

.quantity-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #2a2a2a;
}

.quantity-unit {
  font-size: 11px;
  color: #999;
  white-space: nowrap;
}

.status-display {
  width: 100%;
}

.status-badge {
  display: inline-block;
  width: 90%;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-available {
  background-color: #0a3818;
  color: #51cf66;
  border: 1px solid #51cf66;
}

.status-partial {
  background-color: #2d2006;
  color: #ffd43b;
  border: 1px solid #ffd43b;
}

.status-unavailable {
  background-color: #2b0e0e;
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
}

.status-default {
  background-color: #333;
  color: #999;
  border: 1px solid #555;
}

.checkbox-container input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #3aa757;
  cursor: pointer;
}

.checkbox-container input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.acknowledge-section {
  text-align: center;
  padding-top: 12px;
  border-top: 1px solid #333;
}

.acknowledge-btn {
  background-color: #3aa757;
  color: #e2e2e2;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 160px;
}

.acknowledge-btn:hover:not(:disabled) {
  background-color: #2e8a47;
}

.acknowledge-btn:disabled {
  background-color: #555;
  cursor: not-allowed;
  opacity: 0.6;
}

.status-section {
  text-align: center;
  padding-top: 12px;
  border-top: 1px solid #333;
}

.status-message {
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  display: inline-block;
}

/* Toast Message */
.toast {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 20px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  z-index: 1000;
  animation: slideUp 0.3s ease-out;
  max-width: 90%;
  text-align: center;
}

.toast.success {
  background-color: #0a3818;
  color: #51cf66;
  border: 1px solid #51cf66;
}

.toast.error {
  background-color: #2b0e0e;
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translate(-50%, 20px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

/* Order Summary Styles - Dark Theme Compatible */
.order-summary-section {
  padding: 12px;
  background-color: #1a1a1a;
  border-radius: 6px;
  border: 1px solid #333;
}

.order-summary-section h3 {
  margin: 0 0 12px 0;
  color: #3aa757;
  font-size: 14px;
  font-weight: 600;
}

.summary-details {
  margin-bottom: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #333;
  font-size: 11px;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  font-weight: 500;
  color: #999;
}

.summary-value {
  font-weight: 600;
  color: #e2e2e2;
}

.discount-amount {
  color: #ff6b6b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.clear-discount-btn {
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}

.clear-discount-btn:hover {
  background: #ff5252;
}

.total-row {
  border-top: 1px solid #3aa757;
  margin-top: 6px;
  padding-top: 8px;
}

.total-amount {
  font-size: 14px;
  color: #3aa757;
  font-weight: bold;
}

/* Discount Controls */
.discount-controls {
  margin-top: 12px;
}

.give-discount-btn {
  background: #3aa757;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  transition: background-color 0.3s ease;
  width: 100%;
}

.give-discount-btn:hover {
  background: #2e8a47;
}

.give-discount-btn.active {
  background: #555;
}

.discount-options {
  margin-top: 12px;
  padding: 12px;
  background: #111;
  border-radius: 6px;
  border: 1px solid #333;
}

.discount-type-selection {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.discount-radio {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 11px;
  color: #e2e2e2;
}

.discount-radio input[type="radio"] {
  margin: 0;
  accent-color: #3aa757;
}

.discount-input-section {
  display: flex;
  gap: 8px;
  align-items: center;
}

.discount-input-wrapper {
  position: relative;
  flex: 1;
}

.discount-input {
  width: 100%;
  padding: 6px 30px 6px 8px;
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  color: #e2e2e2;
  font-size: 11px;
  outline: none;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.discount-input:focus {
  border-color: #3aa757;
}

.discount-input::placeholder {
  color: #666;
}

.discount-unit {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-weight: 500;
  font-size: 11px;
}

.apply-discount-btn {
  background: #3aa757;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  min-width: 50px;
  transition: background-color 0.3s ease;
}

.apply-discount-btn:hover:not(:disabled) {
  background: #2e8a47;
}

.apply-discount-btn:disabled {
  background: #555;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .summary-row {
    font-size: 11px;
  }
  
  .discount-type-selection {
    flex-direction: column;
    gap: 8px;
  }
  
  .discount-input-section {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .apply-discount-btn {
    width: 100%;
  }
}

@media (max-width: 320px) {
  .order-summary-section {
    padding: 10px;
  }
  
  .discount-options {
    padding: 10px;
  }
}

/* Responsive Design */
@media (max-width: 320px) {
  .header {
    padding: 10px;
  }
  
  .header h1 {
    font-size: 14px;
  }
  
  .search-section,
  .filter-section,
  .orders-content {
    padding: 10px;
  }
  
  .filter-tabs {
    gap: 4px;
  }
  
  .filter-tab {
    padding: 5px 8px;
    font-size: 11px;
  }
  
  .order-card {
    margin-bottom: 10px;
  }
  
  .order-summary {
    padding: 10px;
  }
}

/* Focus states for accessibility */
.search-input:focus,
.filter-tab:focus,
.see-details-btn:focus,
.acknowledge-btn:focus,
.select-all-btn:focus,
.clear-search-btn:focus {
  outline: 2px solid #3aa757;
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .order-card {
    border-width: 2px;
  }
  
  .filter-tab.active {
    font-weight: 700;
  }
  
  .order-status {
    font-weight: 700;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>